// ────────────────────────────────────────────────────────────────────────────
// Stratum support session — which customer a support user is currently acting
// in.
//
// This is NOT the account store in `../account`. That one tracks which of the
// user's OWN accounts they have switched to, and switching it re-mints the
// token (the Auth0 Action validates membership). A support user is a member of
// nothing: their token carries only `super_user: true`, and the account they
// are working in travels per request in the `X-Account-Key` header. Two
// different mechanisms answering two different questions, kept apart so
// neither quietly acquires the other's powers.
//
// WHY sessionStorage, not localStorage. Every other preference in the suite is
// remembered across visits; a support session must not be. Reopening the
// browser a week later and finding yourself already inside a customer's
// account — with full write access — is how an accidental edit happens. It
// dies with the tab, deliberately.
// ────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'my:support:session';

/** The customer an active support session is pointed at. */
export interface SupportSession {
  /** Account id, sent as `X-Account-Key` on every request. */
  accountId: string;
  /** Account name, for the banner — so the screen always says whose data this is. */
  accountName: string;
  /**
   * Selected organisation id, or `''` at account level. The operational apps
   * need one before anything renders; My Hub does not.
   */
  organisationId: string;
  /** Organisation name, for the banner. */
  organisationName: string;
}

type Listener = (session: SupportSession | null) => void;

let current: SupportSession | null = read();
const listeners = new Set<Listener>();

function read(): SupportSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SupportSession>;
    // The ACCOUNT is what makes a session; the organisation is an app-level
    // concern on top. My Hub works at account level and never picks one, so
    // requiring both here would discard a perfectly good Hub session on
    // reload. The apps that do need an organisation check for it themselves.
    if (!parsed.accountId) return null;
    return {
      accountId: parsed.accountId,
      accountName: parsed.accountName ?? '',
      organisationId: parsed.organisationId ?? '',
      organisationName: parsed.organisationName ?? '',
    };
  } catch {
    // Private windows and blocked site data both throw here. A support user
    // without storage simply picks the account again after a reload.
    return null;
  }
}

/** The active support session, or `null` when there is none. */
export function getSupportSession(): SupportSession | null {
  return current;
}

/**
 * The account id to send as `X-Account-Key`, or `''` when no support session
 * is active. The http transport reads this on every request; it is a plain
 * function rather than a hook because the axios interceptor is not a
 * component.
 */
export function getSupportAccountId(): string {
  return current?.accountId ?? '';
}

/**
 * The organisation a support session is working in, or `null`. Read by each
 * app's `getCurrentOrganisationId`, because a support token carries no
 * `app_roles` claim to read an organisation out of.
 */
export function getSupportOrganisationId(): string | null {
  return current?.organisationId ?? null;
}

/** Enter (or re-point) a support session. */
export function setSupportSession(session: SupportSession): void {
  current = session;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage is a convenience here — the in-memory value is the one the
    // interceptor reads, so a failed write costs a reload, not correctness.
  }
  emit();
}

export interface ClearSupportSessionOptions {
  /**
   * Skip notifying subscribers.
   *
   * <p>
   * For the sign-out path, where a full-page redirect is about to replace
   * everything. Notifying there is not merely wasted work: the session key
   * changes, which remounts the app shell and fires a refetch for every active
   * query — a burst of requests racing the redirect that was about to happen.
   * Nothing needs to re-render when the page is going away.
   * </p>
   */
  silent?: boolean;
}

/** Leave the support session. Call on sign-out and from the banner's exit. */
export function clearSupportSession(options?: ClearSupportSessionOptions): void {
  current = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see setSupportSession */
  }
  if (!options?.silent) emit();
}

/**
 * Adopt a support session handed over in the URL, and strip it from the
 * address bar. Returns whether one was found.
 *
 * <p>
 * My Hub lives on its own origin (my.stratum.ie), so its sessionStorage cannot
 * reach my.tms.stratum.ie — "open this customer in TMS" has to travel as
 * query parameters. Call this at boot, BEFORE the first request fires.
 * </p>
 *
 * <p>
 * It is not validated, and does not need to be: `X-Account-Key` is honoured
 * only for a token that already carries `super_user`, so a customer pasting
 * these parameters moves nothing. The parameters choose a destination; the
 * token is what grants the right to go there.
 * </p>
 *
 * <p>
 * Names are deliberately not carried. They would be stale the moment a
 * customer is renamed, and the receiving app already fetches the account list
 * to draw its picker — `useSupportBar` fills them in from there.
 * </p>
 */
export function consumeSupportHandoff(): boolean {
  if (typeof window === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  const accountId = params.get('support_account');
  if (!accountId) return false;

  setSupportSession({
    accountId,
    accountName: '',
    organisationId: params.get('support_org') ?? '',
    organisationName: '',
  });

  // Out of the address bar: a support session is not something to leave in a
  // shared link, a bookmark or a screenshot.
  params.delete('support_account');
  params.delete('support_org');
  const qs = params.toString();
  window.history.replaceState(
    {},
    '',
    window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
  );

  return true;
}

/**
 * Subscribe to changes. Used by `useSupportSession` so the banner and the
 * pickers re-render together, and so query caches can be cleared when the
 * session moves to another customer.
 */
export function subscribeToSupportSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(): void {
  for (const listener of listeners) listener(current);
}
